/**
 * useLegalPolicy — fetches the CURRENT privacy policy from the server's single
 * source (GET /api/legal/privacy) and hands it to whatever surface renders it:
 * the public /privacy page and the existing-user acceptance gate.
 *
 * There is deliberately no bundled copy of the policy in the client — the text
 * lives in ONE server file (server/assets/legal/privacy-policy.md), so an edit
 * there propagates everywhere with no client change and nothing can drift. The
 * result is cached at module scope so opening the gate and the page in one
 * session only fetches once.
 */
import { useEffect, useState } from 'react';
import api from './api';

export interface PrivacyPolicy {
  version: string;
  effectiveDate: string;
  markdown: string;
}

let cache: PrivacyPolicy | null = null;
let inflight: Promise<PrivacyPolicy> | null = null;

function fetchPolicy(): Promise<PrivacyPolicy> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = api
      .get('/legal/privacy')
      .then(res => { cache = res.data as PrivacyPolicy; return cache; })
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export function usePrivacyPolicy(): { policy: PrivacyPolicy | null; loading: boolean; error: boolean } {
  const [policy, setPolicy] = useState<PrivacyPolicy | null>(cache);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (cache) { setPolicy(cache); return; }
    let live = true;
    fetchPolicy()
      .then(p => { if (live) setPolicy(p); })
      .catch(() => { if (live) setError(true); });
    return () => { live = false; };
  }, []);

  return { policy, loading: !policy && !error, error };
}
